import { NgClass, TitleCasePipe } from '@angular/common';
import { Component, OnInit, inject, output, viewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  FavoriteListService,
  FeatureService,
  GlobalStore,
} from '@maximizer/core/shared/data-access';
import {
  GlobalServicesLoginDetails,
  ListItem,
  UserRights,
} from '@maximizer/core/shared/domain';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { OAuth2Service } from '@maximizer/oauth2/data-access';
import {
  AppDirectoryService,
  AppointmentsSyncSettings,
  ContactsSyncSettings,
  CustomOutlookSyncStatus,
  EmailsSyncSettings,
  ExtendedOutlookSyncStatus,
  OutlookSyncStatus,
  OutlookSyncType,
  SyncHistory,
  TasksSyncSettings,
  UpdateSyncConfiguration,
  UserSyncConfiguration,
  UserSyncConfigurationService,
} from '@maximizer/outlook-sync/data-access';
import {
  OutlookStore,
  generateHandshakeNonce,
  releasePopupOpen,
  respondToHandshakeFromOpener,
  SYNC_CONFIG_HANDSHAKE_NONCE_PARAM,
  SYNC_CONFIG_LANG_PARAM,
  tryClaimPopupOpen,
} from '@maximizer/outlook/shared/data-access';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
  SyncStatusIcon,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';
import { catchError, finalize, forkJoin, of, tap } from 'rxjs';

interface SyncSettingsForm {
  contact: boolean;
  task: boolean;
  appointment: boolean;
  email: boolean;
  favoriteList: string;
  emailScope: EmailScopeId;
}

interface SyncStatus {
  email: {
    code: ExtendedOutlookSyncStatus;
    message?: string; // message for problem status only
  };
  appointment: {
    code: ExtendedOutlookSyncStatus;
    message?: string;
  };
  contact: {
    code: ExtendedOutlookSyncStatus;
    message?: string;
  };
  task: {
    code: ExtendedOutlookSyncStatus;
    message?: string;
  };
}

enum EmailScopeId {
  SyncEverything = 1,
  SyncManual = 2,
}

@Component({
  selector: 'maximizer-outlook-sync-settings',
  templateUrl: './settings.component.html',
  styles: `
    :host {
      .k-dialog-wrapper {
        z-index: 1000;
      }
    }
    .settings-table {
      @apply border border-neutral-10 my-4 max-h-[380px];
      & > * {
        @apply px-2 py-3 flex justify-between;
      }
      & > *:not(:last-child) {
        @apply border-b border-neutral-10;
      }
    }
  `,
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DIALOG,
    KENDO_DROPDOWNS,
    KENDO_INPUTS,
    KENDO_LABEL,
    KENDO_TOOLTIP,
    NgClass,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    TitleCasePipe,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
    SyncStatusIcon,
  ],
})
export class SyncSettingsComponent implements OnInit {
  notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  openSyncSettings = output<boolean>();
  syncSettingsUpdated = output<boolean>();

  private readonly maximizer = 'Maximizer';

  loginDetails?: GlobalServicesLoginDetails;
  form: FormGroupWrapper<SyncSettingsForm>;
  loading = false;
  syncConfiguration?: UserSyncConfiguration;
  userId = '';
  favoriteLists: ListItem<string>[] = [];
  contactSyncFirstSetup = true;
  userRights?: UserRights;
  syncHistories: SyncHistory[] = [];

  disabled = {
    email: false,
    contact: false,
  };

  lastFavoriteList = {
    id: '',
    name: '',
  };

  status: SyncStatus = {
    email: {
      code: CustomOutlookSyncStatus.Paused,
    },
    appointment: {
      code: CustomOutlookSyncStatus.Paused,
    },
    contact: {
      code: CustomOutlookSyncStatus.Paused,
    },
    task: {
      code: CustomOutlookSyncStatus.Paused,
    },
  };

  emailScopeList: ListItem<EmailScopeId>[] = [
    {
      id: EmailScopeId.SyncEverything,
      name: 'outlook.sync.settings.email.scope.syncEverything',
    },
    {
      id: EmailScopeId.SyncManual,
      name: 'outlook.sync.settings.email.scope.syncManual',
    },
  ];

  private readonly outlookSyncService = inject(UserSyncConfigurationService);
  private readonly appDirectoryService = inject(AppDirectoryService);
  private readonly oAuthService = inject(OAuth2Service);
  private readonly favoriteListService = inject(FavoriteListService);
  private readonly featureService = inject(FeatureService);
  readonly outlookStore = inject(OutlookStore);
  readonly globalStore = inject(GlobalStore);

  constructor() {
    this.form = FormWrapperBuilder.group<SyncSettingsForm>({
      contact: [false, Validators.required],
      task: [false, Validators.required],
      appointment: [false, Validators.required],
      email: [false, Validators.required],
      favoriteList: [''],
      emailScope: [
        { value: EmailScopeId.SyncManual, disabled: true },
        {
          validators: [Validators.required],
          updateOn: 'change',
        },
      ],
    });
  }

  ngOnInit(): void {
    this.loginDetails = this.oAuthService.getStorageLoginDetails();
    this.setUserRights();

    this.favoriteListService
      .getAddressBookFavoriteList()
      .pipe(catchError(() => of([])))
      .subscribe((data) => {
        this.favoriteLists = data.map((item) => ({
          id: item.key,
          name: item.name,
        }));

        this.readSettings();
      });
  }

  private setUserRights(): void {
    if (this.globalStore.session()?.user) {
      this.userRights = this.globalStore.session()?.user.rights;

      if (!this.userRights?.documents?.insert) {
        this.disableFormControl('email');
        this.disabled.email = true;
      }

      if (
        !this.userRights?.addressBook?.insert ||
        !this.userRights?.addressBook?.update ||
        !this.userRights.syncContacts
      ) {
        this.disableFormControl('contact');
        this.disabled.contact = true;
      }
    }
  }

  private readSettings(): void {
    const session = this.globalStore.session();
    this.syncConfiguration = this.outlookStore.syncConfiguration();

    if (this.syncConfiguration && session) {
      this.form.patch({
        contact: this.syncConfiguration.contactsSync.enabled,
        task: this.syncConfiguration.tasksSync.enabled,
        appointment: this.syncConfiguration.appointmentsSync.enabled,
        email: this.syncConfiguration.emailsSync.enabled,
        emailScope: this.syncConfiguration.emailsSync.syncEverything
          ? EmailScopeId.SyncEverything
          : EmailScopeId.SyncManual,
      });

      this.userId = session.user.id;
      this.checkContactSyncFirstSetup();
      this.onContactSyncChange();
      this.onEmailSyncChange();
      this.getSyncHistories();
    }
  }

  private getSyncHistories(): void {
    if (!this.syncConfiguration) {
      return;
    }

    const configurationId = this.syncConfiguration.id;
    this.loading = true;

    const syncTypes = [
      OutlookSyncType.Contacts,
      OutlookSyncType.Appointments,
      OutlookSyncType.Tasks,
      OutlookSyncType.Emails,
    ];

    forkJoin(
      syncTypes.map((type) =>
        this.outlookSyncService
          .getHistoryBySyncType(type, configurationId, 1)
          .pipe(catchError(() => of(undefined))),
      ),
    )
      .pipe(
        tap((results) => {
          const anyRequestFailed = results.some(
            (result) => result === undefined,
          );
          if (anyRequestFailed) {
            return;
          }

          const histories = results.flatMap((result) => result ?? []);
          this.syncHistories = histories;
          this.setSyncStatuses();
        }),
        finalize(() => {
          this.loading = false;
        }),
        catchError(() => of(undefined)),
      )
      .subscribe();
  }

  private setSyncStatuses(): void {
    const updateStatus = (
      syncType: OutlookSyncType,
      statusKey: keyof typeof this.status,
    ): void => {
      const item = this.syncHistories.find(
        (item) => item?.syncType === syncType,
      );

      if (item) {
        this.status[statusKey].code = item.syncStatus;
        if (item.syncStatus === OutlookSyncStatus.Problem) {
          this.status[statusKey].message = item.message ?? undefined;
        }
      } else if (this.isSyncTypeEnabled(syncType)) {
        this.status[statusKey].code = OutlookSyncStatus.Syncing;
      }
    };

    if (this.syncConfiguration?.contactsSync?.enabled)
      updateStatus(OutlookSyncType.Contacts, 'contact');
    if (this.syncConfiguration?.appointmentsSync?.enabled)
      updateStatus(OutlookSyncType.Appointments, 'appointment');
    if (this.syncConfiguration?.tasksSync?.enabled)
      updateStatus(OutlookSyncType.Tasks, 'task');
    if (this.syncConfiguration?.emailsSync?.enabled)
      updateStatus(OutlookSyncType.Emails, 'email');
  }

  private isSyncTypeEnabled(syncType: OutlookSyncType): boolean {
    return (
      (syncType === OutlookSyncType.Contacts &&
        !!this.syncConfiguration?.contactsSync?.enabled) ||
      (syncType === OutlookSyncType.Appointments &&
        !!this.syncConfiguration?.appointmentsSync?.enabled) ||
      (syncType === OutlookSyncType.Tasks &&
        !!this.syncConfiguration?.tasksSync?.enabled) ||
      (syncType === OutlookSyncType.Emails &&
        !!this.syncConfiguration?.emailsSync?.enabled)
    );
  }

  private setFavoriteListValues(): void {
    if (!this.syncConfiguration || !this.favoriteLists.length) {
      return;
    }

    if (!this.contactSyncFirstSetup) {
      this.form.group.patchValue({
        favoriteList: this.syncConfiguration.contactsSync.favoriteListKey ?? '',
      });
    } else {
      this.form.group.patchValue({
        favoriteList: this.favoriteLists[0].id,
      });
    }
  }

  private checkContactSyncFirstSetup(): void {
    if (!this.syncConfiguration) return;

    const favoriteListKey =
      this.syncConfiguration.contactsSync.favoriteListKey ?? '';
    const favoriteListName =
      this.syncConfiguration.contactsSync.favoriteListName ?? '';

    if (favoriteListKey !== '' && favoriteListName !== '') {
      this.contactSyncFirstSetup = false;
      this.lastFavoriteList = {
        id: favoriteListKey,
        name: favoriteListName,
      };
    }

    this.setFavoriteListValues();
  }

  private disableFormControl(controlName: string): void {
    const control = this.form.control.get(controlName);
    if (control) {
      control.disable();
    }
  }

  onContactSyncChange(): void {
    const favoriteListControl = this.form.control.get('favoriteList');
    const contactValue = this.form.group.get('contact')?.value;

    if (favoriteListControl) {
      if (contactValue) {
        favoriteListControl.enable();
      } else {
        favoriteListControl.disable();
      }
    }
  }

  onEmailSyncChange(): void {
    const emailControl = this.form.control.get('email');
    const emailScopeControl = this.form.control.get('emailScope');
    const emailEnabled = !!emailControl?.value && !emailControl?.disabled;

    if (!emailScopeControl) {
      return;
    }

    if (emailEnabled) {
      emailScopeControl.enable();
    } else {
      emailScopeControl.disable();
    }
  }

  updateSyncSettings(): void {
    if (this.form.valid && this.syncConfiguration && this.userId) {
      this.loading = true;

      const settings: UpdateSyncConfiguration = {
        maximizerUserUid: this.userId,
        contactsSync: {
          enabled: this.form.value.contact,
          favoriteListKey:
            !this.contactSyncFirstSetup && !this.form.value.contact
              ? this.lastFavoriteList.id
              : (this.form.value.favoriteList ?? ''),
          favoriteListName:
            !this.contactSyncFirstSetup && !this.form.value.contact
              ? this.lastFavoriteList.name
              : this.getFavoriteListNameById(this.form.value.favoriteList),
          outlookCategory: this.maximizer,
        },
        tasksSync: { enabled: this.form.value.task },
        appointmentsSync: { enabled: this.form.value.appointment },
        emailsSync: {
          enabled: this.form.value.email,
          syncEverything:
            this.form.value.emailScope === EmailScopeId.SyncEverything,
        },
      };

      this.outlookSyncService
        .updateConfiguration(settings, this.syncConfiguration.id)
        .pipe(
          catchError(() => {
            this.notification().showMessage(
              'error',
              'outlook.sync.settings.error',
              true,
            );
            return of(null);
          }),
          finalize(() => {
            this.loading = false;
          }),
        )
        .subscribe((result) => {
          if (result) {
            const userSyncConfiguration: UserSyncConfiguration = {
              id: this.syncConfiguration!.id,
              contactsSync: settings.contactsSync as ContactsSyncSettings,
              tasksSync: settings.tasksSync as TasksSyncSettings,
              appointmentsSync:
                settings.appointmentsSync as AppointmentsSyncSettings,
              emailsSync: settings.emailsSync as EmailsSyncSettings,
              enabled: true,
            };

            this.outlookStore.updateSyncConfiguration(userSyncConfiguration);
            this.syncSettingsUpdated.emit(true);
            this.openSyncSettings.emit(false);
            this.notification().showMessage(
              'success',
              'outlook.sync.settings.saved',
              true,
            );
          }
        });
    }
  }

  private getFavoriteListNameById(favoriteListId: string): string {
    return (
      this.favoriteLists.find((item) => favoriteListId === item.id)?.name ?? ''
    );
  }

  async openOutlookSyncHistory(): Promise<void> {
    if (
      this.featureService.isFeatureOn(
        'integration-o365-sync-configuration-shown',
        false,
      )
    ) {
      if (!tryClaimPopupOpen()) {
        return;
      }

      const handshakeNonce = generateHandshakeNonce();
      const childWindow = window.open(
        `${window.location.origin}/sync-configuration?${SYNC_CONFIG_HANDSHAKE_NONCE_PARAM}=${encodeURIComponent(handshakeNonce)}&${SYNC_CONFIG_LANG_PARAM}=${encodeURIComponent(this.outlookStore.language())}`,
        '_blank',
      );
      if (!childWindow) {
        releasePopupOpen();
        this.notification().showMessage(
          'error',
          'outlook.errors.popupBlocked',
          true,
        );
        return;
      }
      respondToHandshakeFromOpener(childWindow, {
        oAuthService: this.oAuthService,
        isCloud: this.outlookStore.isCloud(),
        nonce: handshakeNonce,
      });
      this.openSyncSettings.emit(false);
      return;
    }

    if (!this.loginDetails) {
      return;
    }

    this.appDirectoryService
      .buildAppStoreFormRequest(this.loginDetails, false)
      .pipe(
        tap((result) => {
          if (result) {
            this.openSyncSettings.emit(false);
          }
        }),
        catchError(() => {
          this.notification().showMessage(
            'error',
            'outlook.errors.appStore',
            true,
          );
          return of(null);
        }),
      )
      .subscribe();
  }
}
